import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { LanguageSwitcherComponent } from '../components/language-switcher/language-switcher.component';
import { AuthService } from '../services/auth.service';
import { BrandService } from '../../services/brand-config.service';
import {
  SupportedLanguage,
  TranslationService,
} from '../services/translation.service';
import { TranslatePipe } from '../pipes/translate.pipe';
import { AppComponent } from '../app.component';
import { SessionService } from '../services/session.service';
import { PlatformService } from '../services/platform.service';
import type { User } from '@supabase/supabase-js';

@Component({
  selector: 'app-confirm-email',
  standalone: true,
  imports: [CommonModule, RouterLink, MatProgressSpinner, LanguageSwitcherComponent, TranslatePipe],
  templateUrl: './confirm-email.component.html',
  styleUrl: './confirm-email.component.scss',
})
export class ConfirmEmailComponent {
  /** 'checking' = establishing the session from the email link,
   *  'signin'   = email confirmed but no session in THIS browser (link opened
   *               cross-device/cross-browser) — offer a normal sign-in,
   *  'invalid'  = link expired/reused/opened directly.
   *  Success never renders: we navigate to the loading spinner. */
  state: 'checking' | 'signin' | 'invalid' | 'openApp' = 'checking';
  appLink = '';
  confirmedHere = false;
  currentLanguage: SupportedLanguage = 'en';
  loginUrl: string;

  constructor(
    public auth: AuthService,
    public brand: BrandService,
    public translationService: TranslationService,
    private router: Router,
    private appComponent: AppComponent,
    private session: SessionService,
    private platform: PlatformService,
  ) {
    this.loginUrl = `/${this.appComponent.clerkLocalizationLang || 'en'}/login`;
    this.init();
  }

  private async init() {
    this.currentLanguage = await this.translationService.getCurrentLanguageAsync();
    await this.establishSession();
  }

  // The confirmation email can land here three ways (same shapes as the
  // recovery flow in ResetPasswordComponent):
  //   • ?token_hash=…&type=signup — OTP/token-hash (cross-device, no PKCE
  //     verifier needed). Preferred template format.
  //   • #access_token=…&refresh_token=… — implicit-flow fragment.
  //   • ?code=… — PKCE; supabase-js auto-exchanges it during init, but ONLY in
  //     the browser that started the signup (the verifier lives in its
  //     localStorage). In any other browser the exchange fails — yet GoTrue's
  //     /verify already confirmed the email server-side before redirecting
  //     here, so the right UX is "confirmed, please sign in", not an error.
  private async establishSession(): Promise<void> {
    try {
      // The email template appends "?token_hash=" to RedirectTo, which already
      // carries "?app=1" for native signups: treat any later "?" as "&".
      const href = window.location.href.split('#')[0];
      const qi = href.indexOf('?');
      const fixed = qi < 0 ? href : href.slice(0, qi + 1) + href.slice(qi + 1).replace(/\?/g, '&');
      const url = new URL(fixed + window.location.hash);
      const tokenHash = url.searchParams.get('token_hash');
      const type = url.searchParams.get('type');
      const code = url.searchParams.get('code');
      const hashParams = window.location.hash.startsWith('#')
        ? new URLSearchParams(window.location.hash.slice(1))
        : null;
      const accessToken = hashParams?.get('access_token');
      const errParam =
        url.searchParams.get('error') || hashParams?.get('error');

      // Signup started in the native app: don't consume the token here, hand it
      // to the app (brand://confirm-email?...), whose deep-link handler verifies it.
      const fromApp = url.searchParams.get('app') === '1' || url.pathname.endsWith('/confirm-email-app');
      if (fromApp && tokenHash && !this.platform.isNative) {
        const scheme = this.brand.config.isMarkable ? 'markable' : 'peritale';
        url.searchParams.delete('app');
        this.appLink = `${scheme}://confirm-email${url.search}`;
        // Confirm the email HERE, so it never depends on the app actually
        // receiving the deep link (mail-app browsers often swallow it). The
        // app then gets a ready session; if it still doesn't open, she can
        // sign in with her password because the account is confirmed.
        const t = type === 'email' ? 'email' : 'signup';
        const { data: v, error: vErr } = await this.auth.verifyOtp(tokenHash, t);
        if (!vErr && v?.session) {
          const s = v.session;
          this.appLink = `${scheme}://confirm-email#access_token=${encodeURIComponent(s.access_token)}&refresh_token=${encodeURIComponent(s.refresh_token)}&type=signup`;
          this.confirmedHere = true;
        }
        history.replaceState(null, '', window.location.pathname);
        this.state = 'openApp';
        window.location.href = this.appLink;
        return;
      }
      if (tokenHash) {
        const t = type === 'email' ? 'email' : 'signup';
        await this.auth.verifyOtp(tokenHash, t); // getUser() below decides
      } else if (accessToken) {
        await this.auth.setSession(
          accessToken,
          hashParams?.get('refresh_token') ?? '',
        );
      } else {
        // PKCE ?code= (or none): let the client finish its init exchange.
        await this.auth.waitUntilReady();
      }

      const { data } = await this.auth.getUser();
      // Strip tokens from the address bar so refresh/back doesn't replay them.
      history.replaceState(null, '', window.location.pathname);
      if (data?.user) {
        this.restoreSignupContext(data.user);
        // wireAuth() drives login on the session event; the spinner lands her.
        this.router.navigate([`/${this.currentLanguage}/app/loading`]);
        return;
      }
      this.state = code && !errParam ? 'signin' : 'invalid';
    } catch {
      this.state = 'invalid';
    }
  }

  // Mirror of the metadata stash in SignupComponent: restore the deep link and
  // funnel purpose in THIS browser so the post-login landing continues to her
  // results even though the signup happened elsewhere.
  private restoreSignupContext(user: User): void {
    const md = (user.user_metadata || {}) as Record<string, unknown>;
    const redirect = md['signup_redirect'];
    if (
      typeof redirect === 'string' &&
      redirect.startsWith('/') &&
      !this.session.getRedirectUrl()
    ) {
      this.session.setRedirectUrl(redirect);
      // Copied into this browser's localStorage — consume the one-time metadata
      // now so later logins (any device) route normally.
      this.auth.clearSignupRedirect();
    }
    const purpose = md['purpose'];
    try {
      if (typeof purpose === 'string' && !sessionStorage.getItem('purpose')) {
        sessionStorage.setItem('purpose', purpose);
      }
    } catch {}
  }
}
